'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useAuthStore } from '@/stores/auth'
import { ChevronLeft } from 'lucide-react'
import { Tabs, type TabItem } from '@/components/ui/Tabs'
import DigitalDesignGrid from './_components/DigitalDesignGrid'
import StationeryDesignGrid from './_components/StationeryDesignGrid'

type DesignTab = 'digital' | 'stationery'

export default function DesignsPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const t = useTranslations('designs')
  const { isAuthenticated } = useAuthStore()

  // Get initial tab from URL or default to digital
  const tabParam = searchParams.get('tab') as DesignTab | null
  const initialTab: DesignTab =
    tabParam === 'stationery' ? 'stationery' : 'digital'

  const [activeTab, setActiveTab] = useState<DesignTab>(initialTab)

  const handleTabChange = (tabId: string) => {
    const newTab = tabId as DesignTab
    setActiveTab(newTab)

    // Update URL without navigation
    const params = new URLSearchParams(searchParams.toString())
    if (newTab === 'digital') {
      params.delete('tab')
    } else {
      params.set('tab', newTab)
    }
    const newUrl = params.toString()
      ? `/designs?${params.toString()}`
      : '/designs'
    router.replace(newUrl, { scroll: false })
  }

  const tabs: TabItem[] = [
    {
      id: 'digital',
      label: t('tabs.digital', { defaultValue: 'Digital' }),
    },
    {
      id: 'stationery',
      label: t('tabs.stationery', { defaultValue: 'Stationery' }),
    },
  ]

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header with Back Button */}
      <div className="mb-8 sm:mb-10 lg:mb-12">
        <div className="relative mb-3 sm:mb-4">
          {/* Back Button - Absolute positioned on left */}
          <button
            onClick={() => (isAuthenticated ? router.back() : router.push('/'))}
            className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors z-10 touch-manipulation active:opacity-70 rounded-md cursor-pointer"
            aria-label="Go back"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Title - Centered */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground text-center px-12 sm:px-0">
            {t('pageTitle')}
          </h1>
        </div>

        <div className="text-center px-4">
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('pageDescription')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
          variant="pills"
        />
      </div>

      {/* Tab Content */}
      {activeTab === 'digital' ? (
        <DigitalDesignGrid />
      ) : (
        <StationeryDesignGrid />
      )}

      {/* Popular design links — useful navigation for users and explicit crawler paths to demo pages */}
      <div className="mt-10 pt-6 border-t border-border/40 text-center">
        <p className="text-xs text-muted-foreground/50 mb-2">
          {t('seoSections.featured.heading')}
        </p>
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
          {(
            [
              ['our-love-begins', t('seoSections.featured.ourLoveBegins')],
              ['forever-yours', t('seoSections.featured.foreverYours')],
              ['tying-the-knot', t('seoSections.featured.tyingTheKnot')],
              ['getting-married', t('seoSections.featured.gettingMarried')],
            ] as [string, string][]
          ).map(([id, label]) => (
            <a
              key={id}
              href={`/designs/demo/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground/60 hover:text-primary transition-colors"
            >
              {label}
            </a>
          ))}
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t('requestCustom')}{' '}
          <a
            href="mailto:support@dday.io?subject=RE:%20Request%20Custom%20Design"
            className="text-primary hover:underline font-medium"
          >
            {t('requestCustomLink')}
          </a>
        </p>
      </div>
    </div>
  )
}
